import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatApiErrorDetail } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { useConfirm } from "../components/ConfirmDialog";
import { Plus, Pencil, Trash2, ArrowLeft, Truck, Mail, Phone, User } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "../lib/format";

export default function Suppliers() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/suppliers");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (s) => {
    const ok = await confirm({
      title: "Supprimer ce fournisseur ?",
      description: `« ${s.name} » sera supprimé. Les recettes qui le référencent garderont les prix saisis mais perdront le lien.`,
      confirmText: "Supprimer", destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/suppliers/${s.id}`);
      toast.success("Fournisseur supprimé");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="suppliers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/parametres" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#575A57] flex items-center gap-1.5 hover:text-[#1A1C1A] transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Paramètres
          </Link>
          <h1 className="font-heading text-2xl font-medium text-[#1A1C1A] mt-1 flex items-center gap-2">
            <Truck className="w-6 h-6 text-[#1F4E3D]" /> Fournisseurs
          </h1>
          <p className="text-sm text-[#575A57] mt-1">
            Carnet d'adresses des fournisseurs (Aligro, Manor, marchés, etc.). Sélectionnable depuis chaque ingrédient de fiche technique.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="create-supplier-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouveau fournisseur
            </Button>
          </DialogTrigger>
          <SupplierDialog onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); load(); }} />
        </Dialog>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F9F6] text-xs uppercase tracking-wider text-[#575A57]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Nom</th>
              <th className="text-left px-4 py-3 font-semibold">Contact</th>
              <th className="text-left px-4 py-3 font-semibold">Coordonnées</th>
              <th className="text-left px-4 py-3 font-semibold">Notes</th>
              <th className="text-left px-4 py-3 font-semibold">Créé le</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#A3A6A3]">Chargement…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#A3A6A3]">
                Aucun fournisseur. Créez-en un pour pouvoir l'attribuer aux ingrédients.
              </td></tr>
            ) : items.map((s) => (
              <tr key={s.id} className="border-t border-[#F0F0EB] hover:bg-[#FAFAF7]" data-testid={`supplier-row-${s.id}`}>
                <td className="px-4 py-3 font-medium text-[#1A1C1A]">{s.name}</td>
                <td className="px-4 py-3 text-[#3A3A3A]">
                  {s.contact_name && (
                    <span className="inline-flex items-center gap-1 text-xs"><User className="w-3 h-3 text-[#A3A6A3]" />{s.contact_name}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs">
                  <div className="space-y-0.5">
                    {s.email && <div className="inline-flex items-center gap-1 text-[#3A3A3A]"><Mail className="w-3 h-3 text-[#A3A6A3]" />{s.email}</div>}
                    {s.phone && <div className="inline-flex items-center gap-1 text-[#3A3A3A] ml-3"><Phone className="w-3 h-3 text-[#A3A6A3]" />{s.phone}</div>}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-[#575A57] max-w-[280px] truncate" title={s.notes}>{s.notes || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#575A57]">{fmtDateTime(s.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setEditing(s)} className="p-1.5 rounded hover:bg-[#F0F0EB] text-[#575A57] hover:text-[#1A1C1A]" title="Modifier" data-testid={`edit-supplier-${s.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDelete(s)} className="p-1.5 rounded hover:bg-[#FCEEEA] text-[#575A57] hover:text-[#C05C47]" title="Supprimer" data-testid={`delete-supplier-${s.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        {editing && (
          <SupplierDialog
            supplier={editing}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </Dialog>
    </div>
  );
}

function SupplierDialog({ supplier, onClose, onSaved }) {
  const [form, setForm] = useState(supplier || { name: "", contact_name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (supplier) {
        await api.put(`/suppliers/${supplier.id}`, form);
        toast.success("Fournisseur modifié");
      } else {
        await api.post("/suppliers", form);
        toast.success("Fournisseur créé");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="max-w-md" data-testid="supplier-dialog">
      <DialogHeader>
        <DialogTitle>{supplier ? "Modifier le fournisseur" : "Nouveau fournisseur"}</DialogTitle>
        <DialogDescription>
          Carnet d'adresses simple (nom + contact). Pas d'historique ni de comptabilité — vous mettrez à jour les prix au moment des achats dans la fiche technique.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3 mt-2">
        <div>
          <Label className="mb-1.5 block">Nom *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Aligro, Manor, Marché de Sion..." data-testid="supplier-name" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="mb-1.5 block">Personne de contact</Label>
            <Input value={form.contact_name || ""} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="M. Müller" />
          </div>
          <div>
            <Label className="mb-1.5 block">Téléphone</Label>
            <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+41 …" />
          </div>
        </div>
        <div>
          <Label className="mb-1.5 block">Email</Label>
          <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="contact@…" />
        </div>
        <div>
          <Label className="mb-1.5 block">Notes</Label>
          <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Livraison mardi & vendredi, conditions négociées, etc." />
        </div>
        <DialogFooter className="gap-2 mt-4">
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button type="submit" disabled={submitting || !form.name?.trim()} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="supplier-submit">
            {submitting ? "…" : (supplier ? "Enregistrer" : "Créer")}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
